"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { getOtherNewsData } from "../../../services/TradingMallService";
import type { INewsItem } from "../../../interfaces/News.interface";
import { newsStore } from "../store/newsStore";

interface UseOtherNewsProps {
  initialNews?: INewsItem[];
  filters?: string[];
  limit?: number;
}

interface UseOtherNewsReturn {
  news: INewsItem[];
  isLoading: boolean;
  hasError: boolean;
  page: number;
  showMore: boolean;
  showLess: boolean;
  handleShowMore: () => void;
  handleShowLess: () => void;
  refetch: () => void;
}

export const useOtherNews = ({
  initialNews = [],
  filters = [],
  limit = 5,
}: UseOtherNewsProps): UseOtherNewsReturn => {
  // Check for stored state on mount
  const storedStateRef = useRef<ReturnType<typeof newsStore.getNewsState> | null>(null);
  const hasRestoredRef = useRef(false);

  // Initialize state - will be restored from store in useEffect if available
  const [news, setNews] = useState<INewsItem[]>(initialNews);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [page, setPage] = useState(1);
  const [showMore, setShowMore] = useState(initialNews.length >= limit);
  const [showLess, setShowLess] = useState(false);

  const fetchNews = useCallback(async (pageNum: number, isLoadMore: boolean = false) => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response = await getOtherNewsData({
        filters,
        page: pageNum,
        limit,
      });

      if (response?.news) {
        if (isLoadMore) {
          // Append new news to existing
          setNews((prevNews) => {
            const updated = [...prevNews, ...response.news];
            // Persist to store
            newsStore.saveNewsState(filters, {
              news: updated,
              page: pageNum,
              showMore: response.showMore,
              showLess: response.showLess,
              limit,
            });
            return updated;
          });
        } else {
          // Replace news (initial load or filter change)
          setNews(response.news);
          // Persist to store
          newsStore.saveNewsState(filters, {
            news: response.news,
            page: pageNum,
            showMore: response.showMore,
            showLess: response.showLess,
            limit,
          });
        }

        // Use showMore and showLess from backend response
        setShowMore(response.showMore);
        setShowLess(response.showLess);
      }
    } catch (error) {
      console.error("Failed to fetch other news:", error);
      setHasError(true);
      if (!isLoadMore) {
        setNews([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [filters, limit]);

  // Restore from store on mount - check for stored state for current filters
  useEffect(() => {
    if (hasRestoredRef.current) return;

    const storedState = newsStore.getNewsState(filters);
    storedStateRef.current = storedState;

    if (storedState && storedState.news.length > 0) {
      // Restore from store - this means we're coming back from navigation
      setNews(storedState.news);
      setPage(storedState.page);
      setShowMore(storedState.showMore);
      setShowLess(storedState.showLess);
      setHasError(false);
      hasRestoredRef.current = true;
      return;
    }

    // No stored state found
    hasRestoredRef.current = true;
  }, []);

  // Reset and fetch when filters change
  useEffect(() => {
    // Skip if we haven't completed initial restoration check
    if (!hasRestoredRef.current) return;

    const storedState = newsStore.getNewsState(filters);
    
    if (storedState && storedState.news.length > 0) {
      // Restore from store for these filters
      setNews(storedState.news);
      setPage(storedState.page);
      setShowMore(storedState.showMore);
      setShowLess(storedState.showLess);
      setHasError(false);
      return;
    }

    // No stored state found - use initial news if no filters, otherwise fetch fresh data
    if (filters.length === 0) {
      setPage(1);
      setNews(initialNews);
      setShowMore(initialNews.length >= limit);
      setShowLess(false);
      setHasError(false);
      return;
    }

    // Filters provided but no stored state - fetch fresh data
    setPage(1);
    fetchNews(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // Fetch more news when page changes (but not on initial render or filter change)
  useEffect(() => {
    if (page > 1) {
      fetchNews(page, true);
    }
  }, [page]);

  const handleShowMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const handleShowLess = useCallback(() => {
    // Reset to page 1
    setPage(1);
    // Reset to first page data
    if (filters.length === 0) {
      setNews(initialNews);
      setShowMore(initialNews.length >= limit);
      setShowLess(false);
      // Update store
      newsStore.saveNewsState(filters, {
        news: initialNews,
        page: 1,
        showMore: initialNews.length >= limit,
        showLess: false,
        limit,
      });
    } else {
      fetchNews(1, false);
    }
  }, [filters, initialNews, limit, fetchNews]);

  const refetch = useCallback(() => {
    setPage(1);
    // Clear stored state on refetch
    newsStore.clearNewsState(filters);
    if (filters.length === 0) {
      setNews(initialNews);
      setShowMore(initialNews.length >= limit);
      setShowLess(false);
      setHasError(false);
    } else {
      fetchNews(1, false);
    }
  }, [filters, initialNews, limit, fetchNews]);

  return {
    news,
    isLoading,
    hasError,
    page,
    showMore,
    showLess,
    handleShowMore,
    handleShowLess,
    refetch,
  };
};

export default useOtherNews;